import React, { useEffect, useRef } from "react";
import { useEdit } from "../context/EditContext";

/**
 * EditableText
 * - id: unique string key
 * - children: default text
 * - multiline: allow line breaks (Shift+Enter still works when not multiline)
 * - as: element tag to render
 * - className: additional classes
 */
const EditableText = ({ id, children, multiline = false, as: Tag = "span", className = "", style }) => {
  const { editMode, getValue, setValue } = useEdit();
  const ref = useRef(null);
  const defaultText = typeof children === "string" ? children : String(children ?? "");
  const value = getValue(id, defaultText);

  // Sync DOM only when NOT focused (avoid caret jumping while typing)
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (document.activeElement !== el) {
      const dom = multiline ? el.innerText : el.textContent;
      if (dom !== value) {
        if (multiline) el.innerText = value;
        else el.textContent = value;
      }
    }
  }, [value, multiline]);

  const onInput = () => {
    // Don't setValue on every keystroke to avoid rerender caret jump.
    // We save on blur instead.
  };

  const onBlur = () => {
    const el = ref.current;
    if (!el) return;
    const next = multiline ? el.innerText : el.textContent;
    if (next !== value) setValue(id, next);
  };

  const onKeyDown = (e) => {
    if (!multiline && e.key === "Enter") {
      e.preventDefault();
      e.currentTarget.blur();
    }
  };

  const editableProps = editMode
    ? {
        contentEditable: true,
        suppressContentEditableWarning: true,
        spellCheck: true,
        onInput,
        onBlur,
        onKeyDown,
        className:
          className +
          " outline-none ring-2 ring-[#c47750]/60 hover:ring-[#c47750] focus:ring-[#c47750] focus:ring-4 rounded-sm px-0.5 -mx-0.5 transition-shadow bg-[#fff7e6]/40 cursor-text",
      }
    : { className };

  return (
    <Tag ref={ref} style={style} {...editableProps}>
      {value}
    </Tag>
  );
};

export default EditableText;
